import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Megaphone, Loader2, User } from "lucide-react";

/**
 * AnnouncementDialog — composer for a custom "Announcements" broadcast.
 *
 * Audience picker (iter 44c.4):
 *   • Self toggle — when on, the row is only visible to the actor. Great
 *     for personal reminders.
 *   • Department — All / Content / SEO / Marketing / HR.
 *   • Role bucket — All (everyone in dept) / TLs / CMs / Admins / Dept Admins.
 *
 * Payload → POST /custom-notifications
 *   {title?, message, audience:{scope, department, role}}
 */

const DEPT_OPTIONS = [
  { value: "all",       label: "All departments" },
  { value: "content",   label: "Content" },
  { value: "seo",       label: "SEO" },
  { value: "marketing", label: "Marketing" },
  { value: "hr",        label: "HR" },
];

const ROLE_OPTIONS = [
  { value: "all",        label: "Everyone (all roles)" },
  { value: "tl",         label: "Team Leads" },
  { value: "cm",         label: "Content Managers" },
  { value: "admin",      label: "Admins" },
  { value: "dept_admin", label: "Department Admins" },
];

export default function AnnouncementDialog({ open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [selfOnly, setSelfOnly] = useState(false);
  const [dept, setDept] = useState("all");
  const [role, setRole] = useState("all");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setTitle(""); setMessage("");
    setSelfOnly(false); setDept("all"); setRole("all");
    setSaving(false);
  };

  const buildAudience = () => {
    if (selfOnly) return { scope: "self", department: "all", role: "all" };
    if (dept === "all" && role === "all") return { scope: "all", department: "all", role: "all" };
    return { scope: "role", department: dept, role };
  };

  const audiencePreview = (() => {
    if (selfOnly) return "Only me";
    if (dept === "all" && role === "all") return "Everyone";
    const deptLbl = dept === "all" ? "All" : DEPT_OPTIONS.find((d) => d.value === dept)?.label.replace(" departments", "");
    const roleLbl = ROLE_OPTIONS.find((r) => r.value === role)?.label;
    if (role === "all") return dept === "all" ? "Everyone" : `Everyone in ${deptLbl}`;
    const shortRole = { tl: "TLs", cm: "CMs", admin: "Admins", dept_admin: "Dept Admins" }[role];
    return `${deptLbl} ${shortRole}`;
  })();

  const submit = async () => {
    const msg = message.trim();
    if (!msg) { toast.error("Message can't be empty"); return; }
    if (msg.length > 1000) { toast.error("Message too long (max 1000 chars)"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/custom-notifications", {
        title: title.trim() || undefined,
        message: msg,
        audience: buildAudience(),
      });
      const bannerRow = {
        id: data.id,
        kind: "custom",
        action: "custom_message",
        actor_id: data.actor_id,
        actor_name: data.actor_name,
        actor_role: data.actor_role,
        actor_designation: data.actor_designation,
        audience: data.audience,
        title: data.title,
        description: data.message,
        created_at: data.created_at,
      };
      onCreated && onCreated(bannerRow);
      toast.success("Announcement posted");
      reset();
      onClose && onClose();
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to post announcement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) { reset(); onClose && onClose(); } }}>
      <DialogContent className="sm:max-w-lg" data-testid="announcement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-purple-600" />
            New Announcement
          </DialogTitle>
          <DialogDescription>
            Shows in the "Announcements" banner on the home dashboard of everyone in your chosen audience.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
              Title <span className="normal-case font-normal text-slate-400">(optional)</span>
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Office closed Friday"
              maxLength={120}
              data-testid="announcement-title-input"
            />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
              Message
            </label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write the announcement…"
              rows={4}
              maxLength={1000}
              data-testid="announcement-message-input"
            />
            <div className="mt-1 text-[11px] text-slate-400 text-right">{message.length}/1000</div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Visible to
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Currently: <span className="font-medium text-slate-700">{audiencePreview}</span>
                </div>
              </div>
              <label
                className={`inline-flex items-center gap-2 text-xs font-medium px-2.5 py-1.5 rounded-md cursor-pointer border transition ${
                  selfOnly
                    ? "bg-purple-600 text-white border-purple-600"
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
                data-testid="announcement-self-toggle"
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={selfOnly}
                  onChange={(e) => setSelfOnly(e.target.checked)}
                />
                <User className="h-3.5 w-3.5" />
                Only me (reminder)
              </label>
            </div>

            {!selfOnly && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1 block">
                    Department
                  </label>
                  <Select value={dept} onValueChange={setDept}>
                    <SelectTrigger className="bg-white" data-testid="announcement-dept-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DEPT_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value} data-testid={`announcement-dept-${o.value}`}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1 block">
                    Audience
                  </label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger className="bg-white" data-testid="announcement-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value} data-testid={`announcement-role-${o.value}`}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => { if (!saving) { reset(); onClose && onClose(); } }}
            disabled={saving}
            data-testid="announcement-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || !message.trim()}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="announcement-submit"
          >
            {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Posting…</> : "Post announcement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
